import React, { Suspense, lazy, useEffect, useState, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@alvo/store';
import MainLayout from './layouts/MainLayout';
import { ErrorBoundary } from './components/ErrorBoundary';

// Lazy load pages for code splitting
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ContactsPage = lazy(() => import('./pages/ContactsPage'));
const PipelinePage = lazy(() => import('./pages/PipelinePage'));
const PoliciesPage = lazy(() => import('./pages/PoliciesPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const CommunicationsPage = lazy(() => import('./pages/CommunicationsPage'));
const RetentionPage = lazy(() => import('./pages/RetentionPage'));
const AutomationsPage = lazy(() => import('./pages/AutomationsPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const LicensingPage = lazy(() => import('./pages/LicensingPage'));
const ContractingPage = lazy(() => import("./pages/ContractingPage"));
const PersistencyReportPage = lazy(() => import('./pages/PersistencyReportPage'));
const CompliancePage = lazy(() => import('./pages/CompliancePage'));
const SalesMambaPage = lazy(() => import('./pages/SalesMambaPage'));
const PrecisionLayout = lazy(() => import('./pages/precision/PrecisionLayout'));
const PrecisionCallsPage = lazy(() => import('./pages/precision/PrecisionCallsPage'));
const PrecisionAgentsPage = lazy(() => import('./pages/precision/PrecisionAgentsPage'));
const PrecisionCoachingPage = lazy(() => import('./pages/precision/PrecisionCoachingPage'));
const PrecisionCoachingDetailPage = lazy(() => import('./pages/precision/PrecisionCoachingDetailPage'));
const PrecisionCloserProfilesPage = lazy(() => import('./pages/precision/PrecisionCloserProfilesPage'));
const PrecisionCloserProfileDetailPage = lazy(() => import('./pages/precision/PrecisionCloserProfileDetailPage'));
const PrecisionReportDetailPage = lazy(() => import('./pages/precision/PrecisionReportDetailPage'));
const SuperCloserPage = lazy(() => import('./pages/precision/SuperCloserPage'));

const AccountingPage = lazy(() => import('./pages/AccountingPage'));
const CommissionContractsPage = lazy(() => import('./pages/CommissionContractsPage'));
const NewContactPage = lazy(() => import("./pages/NewContactPage"));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SecuritySettingsPage = lazy(() => import('./pages/SecuritySettingsPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForcePasswordChange = lazy(() => import("./pages/ForcePasswordChange"));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const NotificationCenter = lazy(() => import('./components/NotificationCenter'));
const SetPasswordPage = lazy(() => import("./pages/SetPasswordPage"));

// Lazy load contact detail pages by product type
const MedicareContactDetailPage = lazy(() => import('./pages/MedicareContactDetailPage'));
const ACAContactDetailPage = lazy(() => import('./pages/ACAContactDetailPage'));
const LifeContactDetailPage = lazy(() => import('./pages/LifeContactDetailPage'));
const PrivateContactDetailPage = lazy(() => import('./pages/PrivateContactDetailPage'));
const AncillaryContactDetailPage = lazy(() => import('./pages/AncillaryContactDetailPage'));

// Slim top loading bar — Apple-style, shown during lazy chunk loads
const TopLoadingBar = () => (
  <div style={{
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, height: '2px',
    background: 'linear-gradient(90deg, transparent, #0071e3, transparent)',
    animation: 'loadingSlide 1.2s ease-in-out infinite',
  }}>
    <style>{`@keyframes loadingSlide { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }`}</style>
  </div>
);

// Page transition wrapper — keeps previous page visible during chunk load
const PageLoader = () => (
  <>
    <TopLoadingBar />
    <div style={{
      position: 'absolute', inset: 0,
      background: 'rgba(255,255,255,0.4)',
      backdropFilter: 'blur(1px)',
      transition: 'opacity 0.15s ease',
    }} />
  </>
);

// Protected route wrapper
import { Suspense as FPSuspense } from "react";
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.mustChangePassword) {
    return <Suspense fallback={<div style={{background:"#000",minHeight:"100vh"}} />}><ForcePasswordChange /></Suspense>;
  }

  return <>{children}</>;
};

// Role-based route guard — blocks direct URL access for unauthorized roles
const ROLE_LEVELS: Record<string, number> = {
  OWNER: 100, SUPER_ADMIN: 90, FINANCE_ADMIN: 80, ADMIN: 70, SALES_DIRECTOR: 60, AGENT: 10
};
const RoleGuard: React.FC<{ minRole?: string; allowedRoles?: string[]; children: React.ReactNode }> = ({ minRole, allowedRoles, children }) => {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" replace />;
  
  const userLevel = ROLE_LEVELS[user.role] ?? 0;
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  if (minRole && userLevel < (ROLE_LEVELS[minRole] ?? 0)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

const App: React.FC = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const location = useLocation();
  
  // Background location pattern: when navigating to /contacts/new,
  // the previous page stays rendered behind the modal overlay
  const backgroundLocation = location.state?.backgroundLocation;

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes location={backgroundLocation || location}>
        {/* Public routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
        />
        <Route
          path="/set-password"
          element={<SetPasswordPage />}
        />

        {/* Protected routes with layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<ErrorBoundary><DashboardPage /></ErrorBoundary>} />
          <Route path="contacts" element={<ErrorBoundary><ContactsPage /></ErrorBoundary>} />
          <Route path="contacts/:id/medicare" element={<ErrorBoundary><MedicareContactDetailPage /></ErrorBoundary>} />
          <Route path="contacts/:id/aca" element={<ErrorBoundary><ACAContactDetailPage /></ErrorBoundary>} />
          <Route path="contacts/:id/life" element={<ErrorBoundary><LifeContactDetailPage /></ErrorBoundary>} />
          <Route path="contacts/:id/private" element={<ErrorBoundary><PrivateContactDetailPage /></ErrorBoundary>} />
          <Route path="contacts/:id/ancillary" element={<ErrorBoundary><AncillaryContactDetailPage /></ErrorBoundary>} />
          <Route path="pipeline" element={<ErrorBoundary><PipelinePage /></ErrorBoundary>} />
          <Route path="policies" element={<ErrorBoundary><PoliciesPage /></ErrorBoundary>} />
          <Route path="tasks" element={<ErrorBoundary><TasksPage /></ErrorBoundary>} />
          <Route path="calendar" element={<ErrorBoundary><CalendarPage /></ErrorBoundary>} />
          <Route path="communications" element={<ErrorBoundary><CommunicationsPage /></ErrorBoundary>} />
          <Route path="retention" element={<ErrorBoundary><RetentionPage /></ErrorBoundary>} />
          <Route path="automations" element={<RoleGuard minRole="ADMIN"><ErrorBoundary><AutomationsPage /></ErrorBoundary></RoleGuard>} />
          <Route path="sales-intel" element={<RoleGuard minRole="SALES_DIRECTOR"><ErrorBoundary><PrecisionLayout /></ErrorBoundary></RoleGuard>}>
            <Route index element={<PrecisionCallsPage />} />
            <Route path="agents" element={<PrecisionAgentsPage />} />
            <Route path="coaching" element={<PrecisionCoachingPage />} />
            <Route path="coaching/:id" element={<PrecisionCoachingDetailPage />} />
            <Route path="closers" element={<PrecisionCloserProfilesPage />} />
            <Route path="closers/:id" element={<PrecisionCloserProfileDetailPage />} />
            <Route path="super-closer" element={<SuperCloserPage />} />
            <Route path="report/:id" element={<PrecisionReportDetailPage />} />

          </Route>
          {/* Redirect old /precision URLs */}
          <Route path="precision/*" element={<Navigate to="/sales-intel" replace />} />
          <Route path="analytics" element={<RoleGuard minRole="SALES_DIRECTOR"><ErrorBoundary><AnalyticsPage /></ErrorBoundary></RoleGuard>} />
          <Route path="reports/persistency" element={<RoleGuard allowedRoles={['OWNER','SUPER_ADMIN','ADMIN','FINANCE_ADMIN']}><ErrorBoundary><PersistencyReportPage /></ErrorBoundary></RoleGuard>} />
          <Route path="reports/*" element={<RoleGuard minRole="SALES_DIRECTOR"><ErrorBoundary><ReportsPage /></ErrorBoundary></RoleGuard>} />
          <Route path="documents" element={<ErrorBoundary><DocumentsPage /></ErrorBoundary>} />
          <Route path="licensing" element={<ErrorBoundary><LicensingPage /></ErrorBoundary>} />
          <Route path="compliance" element={<ErrorBoundary><CompliancePage /></ErrorBoundary>} />
          <Route path="accounting" element={<RoleGuard allowedRoles={['OWNER','SUPER_ADMIN','ADMIN','FINANCE_ADMIN']}><ErrorBoundary><AccountingPage /></ErrorBoundary></RoleGuard>} />
          <Route path="commissions" element={<RoleGuard minRole="ADMIN"><ErrorBoundary><CommissionContractsPage /></ErrorBoundary></RoleGuard>} />
          <Route path="contracting" element={<RoleGuard minRole="ADMIN"><ErrorBoundary><ContractingPage /></ErrorBoundary></RoleGuard>} />
          <Route path="team" element={<RoleGuard minRole="ADMIN"><ErrorBoundary><TeamPage /></ErrorBoundary></RoleGuard>} />
          <Route path="settings/*" element={<ErrorBoundary><SettingsPage /></ErrorBoundary>} />
          <Route path="security/*" element={<RoleGuard minRole="ADMIN"><ErrorBoundary><SecuritySettingsPage /></ErrorBoundary></RoleGuard>} />
          <Route path="notifications" element={<ErrorBoundary><NotificationCenter /></ErrorBoundary>} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<ErrorBoundary><NotFoundPage /></ErrorBoundary>} />
      </Routes>
    </Suspense>
  );
};

export default App;
